import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence, useSpring, useMotionValue } from "framer-motion";
import {  MapPinned, ChevronDown, ArrowRight, BadgeCheck, PhoneCall, MonitorPlay, FileCheck2, Star } from "lucide-react";
//import { demandeDemo } from '../api/dossiers';  // adapte le chemin

/* ─── PALETTE ─────────────────────────────────────────────────── */
const C = {
  sand:      "#f5ede0",
  sandDark:  "#e8d8c0",
  terra:     "#c2521a",
  terraLt:   "#e0784a",
  terraDk:   "#8a3510",
  forest:    "#1a3d28",
  forestMid: "#244d35",
  forestLt:  "#2f6645",
  gold:      "#c8902a",
  goldLt:    "#e8b84a",
  ink:       "#0e1a10",
  mist:      "#6e8070",
  cream:     "#faf6ef",
};

/* ─── DATA ─────────────────────────────────────────────────────── */
const PROBLEMS = [
  { icon: "⚠️", title: "Fraudes et escroqueries", desc: "Des familles entières perdent leurs économies sur des terrains vendus plusieurs fois ou soutenus par des titres falsifiés." },
  { icon: "📋", title: "Absence de vérification", desc: "Les acheteurs s'engagent sans enquête indépendante sur le statut juridique réel du bien convoité." },
  { icon: "🌍", title: "Distance et diaspora", desc: "Depuis l'étranger, tout repose sur des intermédiaires. L'incertitude devient la règle, et la confiance un pari." },
  { icon: "⏳", title: "Processus opaques", desc: "Sans preuves, sans suivi, sans clarté : les démarches s'éternisent et l'angoisse s'installe." },
];
const SOLUTIONS = [
  { icon: "🔍", title: "Enquête terrain indépendante", desc: "Des agents vérifiés se déplacent physiquement et documentent chaque constat avec méthode et impartialité." },
  { icon: "✅", title: "Vérification des titres", desc: "Analyse du cadastre, de la chaîne de propriété, des incohérences et des risques juridiques éventuels." },
  { icon: "📱", title: "Suivi en temps réel", desc: "Chaque étape remonte dans un tableau de bord élégant, lisible et rassurant, où que vous soyez." },
  { icon: "📄", title: "Rapport d'expertise certifié", desc: "Une recommandation claire, argumentée, visuelle et exploitable avant toute décision d'achat." },
];
const HOW_STEPS = [
  { num: "01", icon: "📝", title: "Soumettez votre dossier", desc: "Indiquez le terrain, le vendeur et les pièces disponibles. Nous posons immédiatement un cadre de vérification clair." },
  { num: "02", icon: "🔎", title: "Notre équipe enquête", desc: "Nous croisons sources juridiques, constat terrain et analyse documentaire avec une logique de preuve." },
  { num: "03", icon: "📊", title: "Recevez votre rapport", desc: "Vous obtenez une synthèse premium, compréhensible et utile : feu vert, prudence ou alerte rouge." },
];
const STATS = [
  { num: "2 400+", label: "Dossiers traités" },
  { num: "98%", label: "Fraudes évitées" },
  { num: "12", label: "Pays couverts" },
  { num: "72h", label: "Délai moyen" },
];
const TESTIMONIALS = [
  { text: "Depuis Paris, j'ai failli acheter un terrain piégé. TerraTrack a mis au jour un faux titre avant le versement. Ils m'ont sauvé d'une erreur qui m'aurait coûté des années d'épargne.", name: "Kofi Mensah", role: "Ingénieur, Paris — Acheteur au Togo", initial: "K" },
  { text: "Le rapport reçu était limpide, sérieux, presque rassurant émotionnellement. J'ai enfin eu l'impression d'acheter avec des preuves et non avec des promesses.", name: "Aminata Diallo", role: "Médecin, Bruxelles — Acheteuse au Sénégal", initial: "A" },
  { text: "J'ai adoré le suivi. Tout respirait la maîtrise : les statuts, les visuels, les étapes. On sent qu'on n'est plus seul face à un marché opaque.", name: "Jean-Baptiste Houessou", role: "Entrepreneur, Lyon — Acheteur au Bénin", initial: "J" },
];
const PRICING = [
  { name: "Essentiel", price: "89 000", currency: "FCFA", desc: "Pour sécuriser un premier dossier sans complexité excessive.", features: ["Vérification cadastrale","Rapport de base","Suivi en ligne","Support email","Enquête terrain","Rapport certifié"], disabled: [4,5], featured: false },
  { name: "Professionnel", price: "189 000", currency: "FCFA", desc: "L'offre la plus complète pour acheter avec calme et contrôle.", features: ["Vérification cadastrale","Rapport complet certifié","Suivi en temps réel","Support prioritaire 7j/7","Enquête terrain + photos","Consultation juridique incluse"], disabled: [], featured: true, badge: "Le plus choisi" },
  { name: "Entreprise", price: "Sur devis", currency: "", desc: "Pour investisseurs, cabinets, promoteurs et portefeuilles multi-sites.", features: ["Dossiers illimités","Agent dédié","API & intégrations","Rapport personnalisé","Tableau de bord multi-dossiers","Contrat cadre annuel"], disabled: [], featured: false },
];
const FAQS = [
  { q: "Comment TerraTrack vérifie-t-il un terrain ?", a: "Nous combinons enquête terrain, contrôle cadastral, vérification des titres et analyse du risque juridique. Chaque point est documenté dans votre espace." },
  { q: "En combien de temps le rapport est-il prêt ?", a: "Les dossiers standards sortent généralement sous 48 à 72 heures. Les situations litigieuses peuvent demander davantage de vérifications." },
  { q: "Quels pays couvrez-vous ?", a: "Togo, Bénin, Côte d'Ivoire, Sénégal, Ghana, Cameroun, Mali, Burkina Faso, Guinée, RDC, Gabon, avec une expansion progressive." },
  { q: "Que se passe-t-il si un risque est détecté ?", a: "Vous recevez immédiatement une alerte argumentée avec niveau de risque, éléments constatés et recommandation concrète avant tout engagement financier." },
  { q: "Mes données sont-elles protégées ?", a: "Oui. Les données sont chiffrées, cloisonnées et accessibles uniquement aux personnes autorisées sur le dossier." },
];

/* ─── CSS ─────────────────────────────────────────────────────── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;0,700;1,300;1,600&family=DM+Sans:wght@300;400;500;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; }
html { scroll-behavior: smooth; }

body {
  margin: 0;
  font-family: 'DM Sans', sans-serif;
  background: ${C.cream};
  color: ${C.forest};
  overflow-x: hidden;
}

::selection { background: rgba(194,82,26,.2); color: ${C.terraDk}; }

.serif { font-family: 'Cormorant Garamond', serif; }
h1,h2,h3,h4 { font-family: 'Cormorant Garamond', serif; }

/* ── CURSOR ── */
.cursor {
  position: fixed;
  top: 0; left: 0;
  pointer-events: none;
  z-index: 9999;
  mix-blend-mode: multiply;
}
.cursor-ring {
  width: 40px; height: 40px;
  border-radius: 50%;
  border: 1.5px solid ${C.terra};
  opacity: .55;
  transform: translate(-50%, -50%);
  transition: width .3s, height .3s, opacity .3s;
}
.cursor-dot {
  position: absolute;
  top: 50%; left: 50%;
  width: 6px; height: 6px;
  border-radius: 50%;
  background: ${C.terra};
  transform: translate(-50%,-50%);
}

/* ── CANVAS BG ── */
.canvas-bg {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: .55;
}

/* ── NAV ── */
.nav {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 60;
  padding: 20px 0;
  transition: all .4s ease;
}
.nav.solid {
  padding: 12px 0;
  background: rgba(250,246,239,.92);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(194,82,26,.1);
  box-shadow: 0 4px 30px rgba(26,61,40,.06);
}
.nav-inner {
  width: min(1280px, calc(100% - 48px));
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.logo {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: ${C.forest};
}
.logo-mark {
  width: 44px; height: 44px;
  border-radius: 14px;
  display: grid; place-items: center;
  background: ${C.forest};
  box-shadow: 0 8px 24px rgba(26,61,40,.25);
  overflow: hidden;
  position: relative;
}
.logo-mark-inner {
  position: relative;
  z-index: 1;
}
.logo-text {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -.02em;
  line-height: 1;
}
.logo-text em { color: ${C.terra}; font-style: normal; }
.nav-links {
  display: flex;
  align-items: center;
  gap: 4px;
}
.nav-links a {
  color: ${C.forest};
  text-decoration: none;
  padding: 9px 16px;
  border-radius: 10px;
  font-size: .9rem;
  font-weight: 500;
  opacity: .7;
  transition: opacity .2s, background .2s;
}
.nav-links a:hover { opacity: 1; background: rgba(26,61,40,.05); }
.nav-cta {
  background: ${C.forest};
  color: ${C.sand} !important;
  opacity: 1 !important;
  padding: 10px 22px !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 24px rgba(26,61,40,.2);
  font-weight: 600 !important;
}
.nav-cta:hover { background: ${C.forestMid} !important; transform: translateY(-1px); box-shadow: 0 12px 28px rgba(26,61,40,.28) !important; }

/* ── CONTAINER ── */
.container {
  width: min(1280px, calc(100% - 48px));
  margin: 0 auto;
}

/* ── HERO ── */
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 120px 0 80px;
}
.hero-bg-stripes {
  position: absolute;
  inset: 0;
  background:
    repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 80px,
      rgba(194,82,26,.025) 80px,
      rgba(194,82,26,.025) 81px
    );
}
.hero-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
}
.hero-grid {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1.1fr .9fr;
  gap: 60px;
  align-items: center;
}
.hero-tag {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px 8px 10px;
  border-radius: 999px;
  border: 1px solid rgba(194,82,26,.3);
  background: rgba(194,82,26,.08);
  font-size: .82rem;
  font-weight: 600;
  color: ${C.terraDk};
  margin-bottom: 28px;
}
.tag-badge {
  background: ${C.terra};
  color: white;
  font-size: .72rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
}
.hero-h1 {
  font-size: clamp(3.4rem, 7vw, 6.5rem);
  line-height: .92;
  letter-spacing: -.04em;
  margin: 0 0 24px;
  color: ${C.ink};
}
.hero-h1 .accent {
  color: ${C.terra};
  font-style: italic;
}
.hero-h1 .underline-word {
  position: relative;
  display: inline-block;
}
.hero-h1 .underline-word::after {
  content: "";
  position: absolute;
  bottom: 4px; left: 0; right: 0;
  height: 4px;
  border-radius: 2px;
  background: linear-gradient(90deg, ${C.gold}, ${C.terra});
  transform-origin: left;
}
.hero-sub {
  max-width: 560px;
  color: ${C.mist};
  font-size: 1.1rem;
  line-height: 1.75;
  margin-bottom: 36px;
}
.hero-actions {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  align-items: center;
}
.btn-main {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 28px;
  border-radius: 14px;
  background: ${C.forest};
  color: ${C.sand};
  font-weight: 700;
  font-size: .95rem;
  border: none;
  cursor: pointer;
  text-decoration: none;
  transition: transform .25s, box-shadow .25s, background .25s;
  box-shadow: 0 12px 32px rgba(26,61,40,.22);
}
.btn-main:hover { transform: translateY(-3px); box-shadow: 0 18px 40px rgba(26,61,40,.3); background: ${C.forestMid}; }
.btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 24px;
  border-radius: 14px;
  background: transparent;
  color: ${C.forest};
  font-weight: 600;
  font-size: .95rem;
  border: 1.5px solid rgba(26,61,40,.2);
  cursor: pointer;
  text-decoration: none;
  transition: transform .25s, border-color .25s, background .25s;
}
.btn-ghost:hover { transform: translateY(-3px); border-color: ${C.forest}; background: rgba(26,61,40,.04); }
.hero-proof {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  margin-top: 28px;
}
.proof-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: .88rem;
  color: ${C.mist};
  font-weight: 500;
}
.proof-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: ${C.terra};
}

/* ── HERO VISUAL ── */
.hero-visual {
  position: relative;
}
.map-card {
  background: ${C.forest};
  border-radius: 32px;
  overflow: hidden;
  box-shadow: 0 40px 80px rgba(26,61,40,.25), 0 0 0 1px rgba(255,255,255,.08);
  position: relative;
  aspect-ratio: .9;
}
.map-card-inner {
  padding: 28px;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.map-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}
.map-card-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${C.sand};
}
.map-card-status {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(200,144,42,.15);
  border: 1px solid rgba(200,144,42,.3);
  border-radius: 999px;
  padding: 6px 14px;
  font-size: .78rem;
  font-weight: 600;
  color: ${C.goldLt};
}
.status-pulse {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: ${C.gold};
  animation: pulse-gold 1.6s infinite;
}
@keyframes pulse-gold {
  0%,100% { box-shadow: 0 0 0 0 rgba(200,144,42,.7); }
  50% { box-shadow: 0 0 0 8px rgba(200,144,42,0); }
}
.africa-map-zone {
  flex: 1;
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  background:
    radial-gradient(ellipse at 40% 50%, rgba(47,102,69,.5) 0%, transparent 60%),
    linear-gradient(180deg, rgba(26,61,40,.9), rgba(14,26,16,1));
}
.map-grid-lines {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(245,237,224,.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245,237,224,.04) 1px, transparent 1px);
  background-size: 36px 36px;
}
.africa-text {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(7rem,14vw,10rem);
  filter: drop-shadow(0 0 60px rgba(194,82,26,.4));
  animation: float-africa 5s ease-in-out infinite;
}
@keyframes float-africa {
  0%,100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}
.pin-marker {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 8px;
}
.pin-ring {
  width: 14px; height: 14px;
  border-radius: 50%;
  background: ${C.terra};
  position: relative;
}
.pin-ring::after {
  content: "";
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 1.5px solid ${C.terra};
  opacity: .4;
  animation: ripple 2s infinite;
}
@keyframes ripple {
  0% { transform: scale(.7); opacity: .7; }
  100% { transform: scale(1.8); opacity: 0; }
}
.pin-label {
  background: rgba(250,246,239,.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(245,237,224,.14);
  border-radius: 10px;
  padding: 6px 12px;
  font-size: .76rem;
  color: ${C.sand};
  white-space: nowrap;
}
.map-progress {
  margin-top: 18px;
  display: grid;
  gap: 10px;
}
.progress-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.progress-label { font-size: .8rem; color: rgba(245,237,224,.6); flex: none; }
.progress-bar {
  flex: 1;
  height: 5px;
  border-radius: 999px;
  background: rgba(255,255,255,.08);
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, ${C.forestLt}, ${C.gold});
}
.progress-pct { font-size: .78rem; color: ${C.goldLt}; font-weight: 700; flex: none; }

/* floating cards */
.float-badge {
  position: absolute;
  background: ${C.cream};
  border-radius: 16px;
  padding: 14px 18px;
  box-shadow: 0 16px 40px rgba(26,61,40,.18);
  border: 1px solid rgba(194,82,26,.1);
  z-index: 3;
}
.float-badge-icon {
  font-size: 1.4rem;
  margin-bottom: 4px;
}
.float-badge strong {
  display: block;
  font-size: 1.3rem;
  font-family: 'Cormorant Garamond', serif;
  font-weight: 700;
  color: ${C.ink};
}
.float-badge small { color: ${C.mist}; font-size: .78rem; }

/* hero stats */
.hero-stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-top: 48px;
  position: relative;
  z-index: 2;
}
.h-stat {
  padding: 22px 20px;
  border-radius: 20px;
  background: white;
  border: 1px solid rgba(194,82,26,.1);
  box-shadow: 0 4px 20px rgba(26,61,40,.05);
}
.h-stat-num {
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(1.8rem,3vw,2.6rem);
  font-weight: 700;
  color: ${C.terra};
  line-height: 1;
}
.h-stat-label { font-size: .82rem; color: ${C.mist}; margin-top: 5px; }

/* ── SECTIONS ── */
.section {
  position: relative;
  padding: 100px 0;
}
.section.alt {
  background: ${C.forest};
  color: ${C.sand};
}
.section.sand { background: linear-gradient(180deg, ${C.sandDark}, ${C.sand}); }

.section-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(194,82,26,.1);
  border: 1px solid rgba(194,82,26,.2);
  color: ${C.terra};
  font-size: .75rem;
  font-weight: 700;
  letter-spacing: .14em;
  text-transform: uppercase;
  padding: 6px 14px;
  border-radius: 999px;
  margin-bottom: 16px;
}
.section.alt .section-pill {
  background: rgba(245,237,224,.1);
  border-color: rgba(245,237,224,.2);
  color: ${C.sandDark};
}
.section-h2 {
  font-size: clamp(2.4rem, 5vw, 4.2rem);
  line-height: .96;
  letter-spacing: -.04em;
  margin: 0 0 20px;
}
.section-sub {
  max-width: 600px;
  line-height: 1.8;
  opacity: .7;
  font-size: 1rem;
}

/* ── VS GRID ── */
.vs-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 24px;
  align-items: start;
  margin-top: 56px;
}
.vs-col-title {
  font-size: .72rem;
  font-weight: 800;
  letter-spacing: .2em;
  text-transform: uppercase;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.vs-col-title.red { color: #d14040; }
.vs-col-title.green { color: ${C.terra}; }
.vs-card-list { display: grid; gap: 14px; }
.vs-card {
  border-radius: 20px;
  padding: 20px;
  position: relative;
  overflow: hidden;
}
.vs-card.danger {
  background: rgba(209,64,64,.06);
  border: 1px solid rgba(209,64,64,.15);
}
.vs-card.solution {
  background: rgba(47,102,69,.07);
  border: 1px solid rgba(47,102,69,.2);
}
.vs-card-icon {
  font-size: 1.5rem;
  margin-bottom: 10px;
}
.vs-card h4 { margin: 0 0 6px; font-size: 1rem; font-weight: 600; }
.vs-card p { margin: 0; font-size: .9rem; line-height: 1.65; opacity: .75; }
.vs-divider {
  position: sticky;
  top: 120px;
  align-self: start;
  width: 72px; height: 72px;
  border-radius: 50%;
  display: grid; place-items: center;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: ${C.terra};
  background: white;
  border: 2px solid rgba(194,82,26,.2);
  box-shadow: 0 8px 28px rgba(194,82,26,.12);
  letter-spacing: .06em;
}

/* ── STEPS ── */
.steps-timeline {
  margin-top: 60px;
  position: relative;
}
.steps-timeline::before {
  content: "";
  position: absolute;
  left: 50%;
  top: 0; bottom: 0;
  width: 1px;
  background: linear-gradient(to bottom, transparent, rgba(194,82,26,.3), ${C.terra}, rgba(194,82,26,.3), transparent);
  transform: translateX(-50%);
}
.timeline-step {
  display: grid;
  grid-template-columns: 1fr 80px 1fr;
  gap: 20px;
  align-items: center;
  margin-bottom: 48px;
}
.timeline-step:last-child { margin-bottom: 0; }
.step-content {
  background: white;
  border-radius: 24px;
  padding: 28px;
  border: 1px solid rgba(194,82,26,.1);
  box-shadow: 0 8px 32px rgba(26,61,40,.06);
}
.step-content.right { text-align: right; }
.step-num-badge {
  width: 80px; height: 80px;
  border-radius: 50%;
  background: ${C.forest};
  color: ${C.sand};
  display: grid; place-items: center;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.6rem;
  font-weight: 700;
  box-shadow: 0 8px 24px rgba(26,61,40,.25);
  position: relative;
  z-index: 2;
}
.step-emoji { font-size: 2rem; margin-bottom: 12px; }
.step-content h3 { margin: 0 0 8px; font-size: 1.3rem; font-weight: 700; color: ${C.ink}; }
.step-content p { margin: 0; font-size: .92rem; line-height: 1.7; color: ${C.mist}; }
.step-empty { /* empty cell for alt alignment */ }

/* ── TESTIMONIALS ── */
.testi-track {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-top: 52px;
}
.testi-card {
  background: rgba(255,255,255,.05);
  border: 1px solid rgba(245,237,224,.1);
  border-radius: 28px;
  padding: 32px;
  position: relative;
  overflow: hidden;
}
.testi-card::before {
  content: '\u201C';
  position: absolute;
  top: 16px; left: 22px;
  font-family: 'Cormorant Garamond', serif;
  font-size: 6rem;
  line-height: 1;
  color: rgba(245,237,224,.08);
}
.testi-stars { display: flex; gap: 4px; color: ${C.gold}; margin-bottom: 16px; }
.testi-text { font-size: .96rem; line-height: 1.8; opacity: .8; position: relative; z-index: 1; }
.testi-author { display: flex; align-items: center; gap: 14px; margin-top: 24px; }
.testi-avatar {
  width: 48px; height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${C.terra}, ${C.gold});
  display: grid; place-items: center;
  font-weight: 700;
  font-size: 1rem;
  color: white;
  flex: none;
}
.testi-name { font-weight: 600; font-size: .9rem; }
.testi-role { font-size: .8rem; opacity: .5; margin-top: 2px; }

/* ── PRICING ── */
.pricing-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
  margin-top: 56px;
}
.price-card {
  border-radius: 28px;
  padding: 32px;
  border: 1.5px solid rgba(26,61,40,.1);
  background: white;
  position: relative;
  transition: transform .3s, box-shadow .3s;
}
.price-card:hover { transform: translateY(-6px); box-shadow: 0 24px 60px rgba(26,61,40,.12); }
.price-card.featured {
  background: ${C.forest};
  color: ${C.sand};
  border-color: ${C.forest};
  box-shadow: 0 24px 60px rgba(26,61,40,.25);
  transform: translateY(-8px);
}
.price-card.featured:hover { transform: translateY(-14px); }
.price-badge {
  position: absolute;
  top: -12px; left: 24px;
  background: ${C.terra};
  color: white;
  font-size: .72rem;
  font-weight: 800;
  letter-spacing: .06em;
  padding: 5px 14px;
  border-radius: 999px;
  box-shadow: 0 6px 16px rgba(194,82,26,.3);
}
.price-tier {
  font-size: .72rem;
  font-weight: 800;
  letter-spacing: .2em;
  text-transform: uppercase;
  opacity: .5;
  margin-bottom: 8px;
}
.price-amount {
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2rem, 4vw, 2.8rem);
  font-weight: 700;
  line-height: 1;
  margin-bottom: 6px;
}
.price-amount small {
  font-size: 1rem;
  opacity: .5;
  font-weight: 400;
  margin-right: 6px;
}
.price-desc { font-size: .9rem; line-height: 1.65; opacity: .6; margin-bottom: 24px; }
.price-features { list-style: none; padding: 0; margin: 0 0 28px; display: grid; gap: 11px; }
.price-features li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: .9rem;
  opacity: .85;
}
.price-features li.off { opacity: .3; }
.check-icon {
  width: 22px; height: 22px;
  border-radius: 50%;
  display: grid; place-items: center;
  font-size: .75rem;
  font-weight: 900;
  flex: none;
}
.check-icon.on { background: rgba(47,102,69,.15); color: ${C.forestLt}; }
.price-card.featured .check-icon.on { background: rgba(245,237,224,.15); color: ${C.sandDark}; }
.check-icon.off-icon { background: rgba(0,0,0,.04); color: rgba(0,0,0,.2); }
.btn-price {
  display: block;
  width: 100%;
  padding: 15px;
  border-radius: 14px;
  font-weight: 700;
  font-size: .92rem;
  text-align: center;
  cursor: pointer;
  border: none;
  transition: transform .2s, box-shadow .2s;
}
.btn-price.dark { background: ${C.forest}; color: ${C.sand}; box-shadow: 0 8px 24px rgba(26,61,40,.2); }
.btn-price.dark:hover { transform: translateY(-2px); box-shadow: 0 14px 32px rgba(26,61,40,.28); }
.btn-price.light { background: ${C.sand}; color: ${C.forest}; }
.btn-price.light:hover { transform: translateY(-2px); }
.btn-price.outline-price {
  background: transparent;
  border: 1.5px solid rgba(26,61,40,.2);
  color: ${C.forest};
}
.btn-price.outline-price:hover { border-color: ${C.forest}; }

/* ── FAQ ── */
.faq-list { max-width: 860px; margin: 52px auto 0; display: grid; gap: 12px; }
.faq-item {
  border-radius: 20px;
  border: 1px solid rgba(26,61,40,.1);
  background: white;
  overflow: hidden;
}
.faq-btn {
  width: 100%; background: transparent; border: 0;
  color: ${C.forest}; padding: 22px 26px;
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px; text-align: left; cursor: pointer;
  font: 600 1rem 'DM Sans', sans-serif;
  transition: background .2s;
}
.faq-btn:hover { background: rgba(26,61,40,.02); }
.faq-answer { padding: 0 26px 22px; color: ${C.mist}; line-height: 1.8; font-size: .95rem; }

/* ── DEMO / FORM ── */
.demo-section {
  background: linear-gradient(135deg, ${C.forest} 0%, ${C.forestMid} 50%, ${C.ink} 100%);
  position: relative;
  overflow: hidden;
}
.demo-pattern {
  position: absolute;
  inset: 0;
  background-image:
    repeating-linear-gradient(0deg, rgba(245,237,224,.03) 0px, rgba(245,237,224,.03) 1px, transparent 1px, transparent 60px),
    repeating-linear-gradient(90deg, rgba(245,237,224,.03) 0px, rgba(245,237,224,.03) 1px, transparent 1px, transparent 60px);
}
.demo-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: center;
  position: relative;
  z-index: 2;
}
.demo-copy { color: ${C.sand}; }
.demo-copy .section-h2 { color: ${C.sand}; }
.demo-copy .section-sub { color: rgba(245,237,224,.7); }
.benefits-list { display: grid; gap: 16px; margin-top: 32px; }
.benefit {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 18px;
  border-radius: 18px;
  background: rgba(245,237,224,.06);
  border: 1px solid rgba(245,237,224,.1);
}
.benefit-icon {
  width: 46px; height: 46px;
  border-radius: 14px;
  background: rgba(194,82,26,.2);
  color: ${C.terraLt};
  display: grid; place-items: center;
  flex: none;
}
.benefit strong { display: block; color: ${C.sand}; font-weight: 600; margin-bottom: 4px; }
.benefit p { margin: 0; font-size: .88rem; color: rgba(245,237,224,.6); line-height: 1.65; }

.form-box {
  background: ${C.cream};
  border-radius: 28px;
  padding: 36px;
  box-shadow: 0 40px 80px rgba(0,0,0,.3);
}
.form-box h3 { font-size: 1.9rem; color: ${C.ink}; margin: 0 0 6px; }
.form-box .sub { color: ${C.mist}; font-size: .88rem; margin-bottom: 28px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.field { display: grid; gap: 7px; margin-bottom: 14px; }
.field.full { grid-column: 1/-1; }
.field label { font-size: .73rem; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: ${C.mist}; }
.field input,
.field select,
.field textarea {
  width: 100%; border-radius: 12px;
  border: 1.5px solid rgba(26,61,40,.12);
  background: white;
  color: ${C.ink};
  padding: 12px 14px;
  font: inherit;
  font-size: .95rem;
  transition: border-color .2s;
  outline: none;
}
.field input:focus,
.field select:focus,
.field textarea:focus { border-color: ${C.terra}; box-shadow: 0 0 0 3px rgba(194,82,26,.1); }
.field textarea { resize: vertical; min-height: 100px; }
.success-box {
  text-align: center;
  padding: 40px 20px;
}
.success-icon {
  width: 72px; height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${C.forest}, ${C.forestLt});
  color: ${C.sand};
  display: grid; place-items: center;
  margin: 0 auto 20px;
}
.success-box h3 { color: ${C.ink}; font-size: 2rem; margin: 0 0 10px; }
.success-box p { color: ${C.mist}; line-height: 1.8; }

/* ── FOOTER ── */
.footer {
  background: ${C.ink};
  color: ${C.sand};
  padding: 72px 0 40px;
}
.footer-grid {
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr 1fr;
  gap: 40px;
  margin-bottom: 56px;
}
.footer-brand p { color: rgba(245,237,224,.5); font-size: .9rem; line-height: 1.8; margin-top: 16px; max-width: 320px; }
.footer-col strong { display: block; font-size: .72rem; font-weight: 800; letter-spacing: .2em; text-transform: uppercase; margin-bottom: 16px; color: rgba(245,237,224,.4); }
.footer-col ul { list-style: none; padding: 0; margin: 0; display: grid; gap: 10px; }
.footer-col a { color: rgba(245,237,224,.65); text-decoration: none; font-size: .9rem; transition: color .2s; }
.footer-col a:hover { color: ${C.sand}; }
.footer-bottom {
  border-top: 1px solid rgba(245,237,224,.07);
  padding-top: 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: .82rem;
  color: rgba(245,237,224,.3);
}
.footer-bottom a { color: rgba(245,237,224,.4); text-decoration: none; transition: color .2s; }
.footer-bottom a:hover { color: ${C.sand}; }

/* footer logo */
.footer .logo-mark { background: rgba(245,237,224,.08); box-shadow: none; }
.footer .logo-text { color: ${C.sand}; }

/* ── RESPONSIVE ── */
@media (max-width: 1024px) {
  .hero-grid { grid-template-columns: 1fr; }
  .hero-visual { display: none; }
  .hero-stats-row { grid-template-columns: repeat(2,1fr); }
  .steps-timeline::before { display: none; }
  .timeline-step { grid-template-columns: 1fr; }
  .timeline-step .step-empty { display: none; }
  .step-num-badge { width: 56px; height: 56px; font-size: 1.2rem; }
  .step-content.right { text-align: left; }
  .testi-track { grid-template-columns: 1fr; }
  .pricing-grid { grid-template-columns: 1fr; }
  .price-card.featured { transform: none; }
  .demo-grid { grid-template-columns: 1fr; }
  .footer-grid { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 640px) {
  .vs-grid { grid-template-columns: 1fr; }
  .vs-divider { display: none; }
  .hero-stats-row { grid-template-columns: repeat(2,1fr); }
  .nav-links { display: none; }
}
`;

/* ─── HELPERS ─────────────────────────────────────────────────── */
function useScrollReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.12 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, y = 28, scale = 1 }) {
  const [ref, visible] = useScrollReveal();
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y, scale }} animate={visible ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: .65, delay, ease: [.22, 1, .36, 1] }}>
      {children}
    </motion.div>
  );
}

/* animated counter */
function Counter({ target, suffix = "" }) {
  const [val, setVal] = useState(0);
  const [ref, visible] = useScrollReveal();
  useEffect(() => {
    if (!visible) return;
    const num = parseFloat(target.replace(/\s/g, "").replace("+", "").replace("%", "").replace("h", ""));
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / 1400, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(ease * num));
      if (p < 1) requestAnimationFrame(step);
      else setVal(num);
    };
    requestAnimationFrame(step);
  }, [visible, target]);
  const raw = target.replace(/\s/g, "");
  const hasPlus = raw.includes("+");
  const hasPct = raw.includes("%");
  const hasH = raw.includes("h");
  return (
    <span ref={ref}>
      {hasPct || hasH ? "" : val > 999 ? (val / 1).toLocaleString("fr") : val}
      {hasPct ? `${val}%` : ""}
      {hasH ? `${val}h` : ""}
      {hasPlus && !hasPct && !hasH ? "+" : ""}
    </span>
  );
}

/* custom cursor */
function Cursor() {
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const xSpring = useSpring(x, { stiffness: 100, damping: 18 });
  const ySpring = useSpring(y, { stiffness: 100, damping: 18 });
  useEffect(() => {
    const move = (e) => { x.set(e.clientX); y.set(e.clientY); };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);
  return (
    <motion.div className="cursor" style={{ x: xSpring, y: ySpring }}>
      <div className="cursor-ring" />
      <div className="cursor-dot" />
    </motion.div>
  );
}

/* animated background canvas */
function BgCanvas() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let W = canvas.width = window.innerWidth;
    let H = canvas.height = window.innerHeight;
    const particles = Array.from({ length: 28 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      r: Math.random() * 2.5 + .5,
      vx: (Math.random() - .5) * .18,
      vy: (Math.random() - .5) * .18,
      opacity: Math.random() * .25 + .05,
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(194,82,26,${p.opacity})`;
        ctx.fill();
      });
      // draw lines
      particles.forEach((p, i) => {
        particles.slice(i + 1).forEach(q => {
          const d = Math.hypot(p.x - q.x, p.y - q.y);
          if (d < 160) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(26,61,40,${(.04) * (1 - d / 160)})`;
            ctx.lineWidth = .6;
            ctx.stroke();
          }
        });
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    const resize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} className="canvas-bg" />;
}

/* ─── MAIN ─────────────────────────────────────────────────────── */
export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({ prenom:"", nom:"", email:"", telephone:"", pays:"", message:"" });
  const [formSent, setFormSent] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const demoRef = useRef(null);

  const scrollToDemo = useCallback(() => {
    demoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

const API_URL = process.env.REACT_APP_API_URL || 'https://terratrack-production.up.railway.app/api';
const handleForm = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
        const res = await fetch(`${API_URL}/dossiers/demo/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                prenom:  formData.prenom,
                nom:     formData.nom,
                email:   formData.email,
                tel:     formData.telephone,
                pays:    formData.pays,
                message: formData.message,
            }),
        });
        if (!res.ok) throw new Error();
        setFormSent(true);
    } catch {
        alert('Erreur lors de l\'envoi, réessayez.');
    } finally {
        setFormLoading(false);
    }
};
  const HERO_ANIM = { hidden: { opacity: 0, y: 30 }, visible: (i) => ({ opacity: 1, y: 0, transition: { duration: .7, delay: i * .12, ease: [.22,1,.36,1] } }) };

  return (
    <div style={{ position: "relative" }}>
      <style>{css}</style>
      <Cursor />
      <BgCanvas />

      {/* ── NAV ── */}
      <nav className={`nav${scrolled ? " solid" : ""}`}>
        <div className="nav-inner">
            <a href="/#" className="logo">
            <div className="logo-mark">
              <div className="logo-mark-inner"><MapPinned size={20} color={C.sand} /></div>
            </div>
            <div className="logo-text serif">Terra<em>Track</em></div>
          </a>
          <div className="nav-links">
            {[["#problemes","Problème"],["#comment","Comment"],["#tarifs","Tarifs"],["#faq","FAQ"]].map(([href,label]) => (
              <a key={href} href={href}>{label}</a>
            ))}
            <a href="#demo" className="nav-cta" onClick={(e) => { e.preventDefault(); scrollToDemo(); }}>
              Demander une démo
            </a>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-bg-stripes" />
        <div className="hero-blob" style={{ width:600, height:600, top:-200, right:-100, background:`radial-gradient(circle, rgba(194,82,26,.07), transparent 70%)` }} />
        <div className="hero-blob" style={{ width:400, height:400, bottom:-100, left:-100, background:`radial-gradient(circle, rgba(47,102,69,.06), transparent 70%)` }} />

        <div className="container">
          <div className="hero-grid">
            <div>
              <motion.div custom={0} variants={HERO_ANIM} initial="hidden" animate="visible">
                <div className="hero-tag">
                  <span className="tag-badge">Nouveau</span>
                  Vérification foncière certifiée en Afrique
                </div>
              </motion.div>

              <motion.h1 className="hero-h1" custom={1} variants={HERO_ANIM} initial="hidden" animate="visible">
                Achetez votre<br />
                terrain
                <span className="accent"> sans</span>
                <br />
                <span className="underline-word">
                  risque
                  <motion.span
                    style={{ position:"absolute", bottom:4, left:0, right:0, height:4, borderRadius:2, background:`linear-gradient(90deg, ${C.gold}, ${C.terra})`, transformOrigin:"left" }}
                    initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: .9, duration: .7, ease: [.22,1,.36,1] }}
                  />
                </span>
              </motion.h1>

              <motion.p className="hero-sub" custom={2} variants={HERO_ANIM} initial="hidden" animate="visible">
                TerraTrack vérifie indépendamment chaque terrain, valide les titres de propriété et vous remet un rapport d'expertise certifié — pour acheter avec des preuves, pas des promesses.
              </motion.p>

              <motion.div className="hero-actions" custom={3} variants={HERO_ANIM} initial="hidden" animate="visible">
                <button className="btn-main" onClick={scrollToDemo}>
                  Demander une vérification <ArrowRight size={16} />
                </button>
                <a href="#comment" className="btn-ghost">
                  Comment ça marche
                </a>
              </motion.div>

              <motion.div className="hero-proof" custom={4} variants={HERO_ANIM} initial="hidden" animate="visible">
                {[
                  { dot:true, text:"2 400+ dossiers traités" },
                  { dot:true, text:"98% de fraudes évitées" },
                  { dot:true, text:"12 pays couverts" },
                ].map(({ dot, text }) => (
                  <div key={text} className="proof-item">
                    {dot && <div className="proof-dot" />}
                    {text}
                  </div>
                ))}
              </motion.div>
            </div>

            {/* hero visual */}
            <motion.div className="hero-visual" initial={{ opacity:0, x:40, scale:.95 }} animate={{ opacity:1, x:0, scale:1 }} transition={{ duration:.9, delay:.3, ease:[.22,1,.36,1] }}>
              <div className="map-card">
                <div className="map-card-inner">
                  <div className="map-card-header">
                    <span className="map-card-title serif">Rapport #TT-2847</span>
                    <span className="map-card-status">
                      <span className="status-pulse" />
                      En cours
                    </span>
                  </div>

                  <div className="africa-map-zone">
                    <div className="map-grid-lines" />
                    <div className="africa-text">🌍</div>
                    {[
                      { top:"30%", left:"35%", label:"Lomé, Togo" },
                      { top:"48%", left:"20%", label:"Dakar, Sénégal" },
                      { top:"55%", left:"55%", label:"Cotonou, Bénin" },
                    ].map(({ top, left, label }, i) => (
                      <motion.div key={label} className="pin-marker" style={{ top, left }}
                        initial={{ opacity:0, scale:0 }} animate={{ opacity:1, scale:1 }}
                        transition={{ delay: 1 + i * .3, duration:.5, ease:[.22,1,.36,1] }}>
                        <div className="pin-ring" />
                        <div className="pin-label">{label}</div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="map-progress">
                    {[["Vérification cadastrale","100%"],["Enquête terrain","78%"],["Analyse juridique","45%"]].map(([label, pct]) => (
                      <div key={label} className="progress-row">
                        <span className="progress-label">{label}</span>
                        <div className="progress-bar">
                          <motion.div className="progress-fill" style={{ width: pct }}
                            initial={{ width:0 }} animate={{ width: pct }} transition={{ delay:1.2, duration:1, ease:[.22,1,.36,1] }} />
                        </div>
                        <span className="progress-pct">{pct}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* floating badges */}
              <motion.div className="float-badge" style={{ bottom:110, left:-40 }}
                initial={{ opacity:0, x:-20 }} animate={{ opacity:1, x:0 }} transition={{ delay:1.1, duration:.6 }}>
                <div className="float-badge-icon">🛡️</div>
                <strong>98%</strong>
                <small>Fraudes évitées</small>
              </motion.div>

              <motion.div className="float-badge" style={{ top:60, right:-36 }}
                initial={{ opacity:0, x:20 }} animate={{ opacity:1, x:0 }} transition={{ delay:1.3, duration:.6 }}>
                <div className="float-badge-icon">⚡</div>
                <strong>72h</strong>
                <small>Rapport livré</small>
              </motion.div>
            </motion.div>
          </div>

          {/* stats row */}
          <div className="hero-stats-row">
            {STATS.map(({ num, label }, i) => (
              <Reveal key={label} delay={i * .08} y={16}>
                <div className="h-stat">
                  <div className="h-stat-num">
                    <Counter target={num} />
                  </div>
                  <div className="h-stat-label">{label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROBLÈME VS SOLUTION ── */}
      <section className="section" id="problemes">
        <div className="container">
          <Reveal>
            <div className="section-pill">Le problème</div>
            <h2 className="section-h2">Le marché foncier africain<br /><em style={{color:C.terra}}>mérite mieux</em></h2>
            <p className="section-sub">Des millions de transactions se font dans l'opacité. Nous avons bâti le système qui apporte la preuve là où règne la confiance aveugle.</p>
          </Reveal>

          <div className="vs-grid">
            <div>
              <div className="vs-col-title red">⛔ Sans TerraTrack</div>
              <div className="vs-card-list">
                {PROBLEMS.map((p, i) => (
                  <Reveal key={p.title} delay={i * .06}>
                    <div className="vs-card danger">
                      <div className="vs-card-icon">{p.icon}</div>
                      <h4>{p.title}</h4>
                      <p>{p.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <Reveal>
              <div className="vs-divider">VS</div>
            </Reveal>

            <div>
              <div className="vs-col-title green">✅ Avec TerraTrack</div>
              <div className="vs-card-list">
                {SOLUTIONS.map((s, i) => (
                  <Reveal key={s.title} delay={i * .06}>
                    <div className="vs-card solution">
                      <div className="vs-card-icon">{s.icon}</div>
                      <h4>{s.title}</h4>
                      <p>{s.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── COMMENT ── */}
      <section className="section alt" id="comment">
        <div className="container">
          <Reveal>
            <div className="section-pill">Processus</div>
            <h2 className="section-h2" style={{ color: C.sand }}>Trois étapes.<br /><em style={{ color: C.goldLt }}>Une certitude.</em></h2>
            <p className="section-sub" style={{ color: "rgba(245,237,224,.65)" }}>Notre méthode est claire, documentée et pensée pour éliminer le risque à chaque stade.</p>
          </Reveal>

          <div className="steps-timeline">
            {HOW_STEPS.map((step, i) => {
              const isEven = i % 2 === 0;
              return (
                <Reveal key={step.num} delay={i * .12}>
                  <div className="timeline-step">
                    {isEven ? (
                      <>
                        <div className={`step-content${isEven ? "" : " right"}`}>
                          <div className="step-emoji">{step.icon}</div>
                          <h3>{step.title}</h3>
                          <p>{step.desc}</p>
                        </div>
                        <div className="step-num-badge">{step.num}</div>
                        <div className="step-empty" />
                      </>
                    ) : (
                      <>
                        <div className="step-empty" />
                        <div className="step-num-badge">{step.num}</div>
                        <div className="step-content right">
                          <div className="step-emoji">{step.icon}</div>
                          <h3>{step.title}</h3>
                          <p>{step.desc}</p>
                        </div>
                      </>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="section alt">
        <div className="container">
          <Reveal>
            <div className="section-pill">Témoignages</div>
            <h2 className="section-h2" style={{ color: C.sand }}>Ils ont acheté<br /><em style={{ color: C.goldLt }}>avec confiance</em></h2>
          </Reveal>

          <div className="testi-track">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * .1}>
                <div className="testi-card">
                  <div className="testi-stars">
                    {Array(5).fill(0).map((_, j) => <Star key={j} size={14} fill="currentColor" />)}
                  </div>
                  <p className="testi-text">{t.text}</p>
                  <div className="testi-author">
                    <div className="testi-avatar">{t.initial}</div>
                    <div>
                      <div className="testi-name">{t.name}</div>
                      <div className="testi-role">{t.role}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section className="section" id="tarifs">
        <div className="container">
          <Reveal>
            <div className="section-pill">Tarifs</div>
            <h2 className="section-h2">Investissez dans<br /><em style={{ color: C.terra }}>la certitude</em></h2>
            <p className="section-sub">Chaque offre inclut notre méthodologie de vérification éprouvée. Choisissez votre niveau de couverture.</p>
          </Reveal>

          <div className="pricing-grid">
            {PRICING.map((plan, i) => (
              <Reveal key={plan.name} delay={i * .1}>
                <div className={`price-card${plan.featured ? " featured" : ""}`}>
                  {plan.badge && <div className="price-badge">{plan.badge}</div>}
                  <div className="price-tier">{plan.name}</div>
                  <div className="price-amount">
                    {plan.currency && <small>{plan.currency}</small>}
                    {plan.price}
                  </div>
                  <p className="price-desc">{plan.desc}</p>
                  <ul className="price-features">
                    {plan.features.map((f, fi) => (
                      <li key={f} className={plan.disabled?.includes(fi) ? "off" : ""}>
                        <span className={`check-icon ${plan.disabled?.includes(fi) ? "off-icon" : "on"}`}>
                          {plan.disabled?.includes(fi) ? "✕" : "✓"}
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button className={`btn-price ${plan.featured ? "light" : "dark"}`} onClick={scrollToDemo}>
                    {plan.price === "Sur devis" ? "Nous contacter" : "Commencer"}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="section sand" id="faq">
        <div className="container" style={{ textAlign:"center" }}>
          <Reveal>
            <div className="section-pill" style={{ margin: "0 auto 16px" }}>FAQ</div>
            <h2 className="section-h2">Questions fréquentes</h2>
          </Reveal>

          <div className="faq-list">
            {FAQS.map((faq, i) => (
              <Reveal key={faq.q} delay={i * .04}>
                <div className="faq-item">
                  <button className="faq-btn" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                    <span>{faq.q}</span>
                    <motion.span animate={{ rotate: openFaq === i ? 180 : 0 }} transition={{ duration:.25 }}>
                      <ChevronDown size={18} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === i && (
                      <motion.div initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
                        exit={{ height:0, opacity:0 }} transition={{ duration:.28 }} style={{ overflow:"hidden" }}>
                        <div className="faq-answer">{faq.a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEMO / FORM ── */}
      <section className="section demo-section" id="demo" ref={demoRef}>
        <div className="demo-pattern" />
        <div className="container demo-grid">
          <Reveal>
            <div className="demo-copy">
              <div className="section-pill">Démo gratuite</div>
              <h2 className="section-h2">Prêt à acheter<br /><em style={{ color: C.goldLt }}>en confiance ?</em></h2>
              <p className="section-sub">Une démonstration pensée pour convaincre vite, rassurer fort et faire ressentir la valeur avant même le premier dossier.</p>

              <div className="benefits-list">
                {[
                  { icon: <PhoneCall size={20} />, title: "Appel de découverte", desc: "30 minutes pour cadrer votre projet, vos doutes et vos priorités." },
                  { icon: <MonitorPlay size={20} />, title: "Démo de la plateforme", desc: "Une visite fluide, visuelle et orientée preuves, pas seulement fonctionnalités." },
                  { icon: <FileCheck2 size={20} />, title: "Dossier test offert", desc: "Une première expérience concrète à tarif préférentiel." },
                ].map((item) => (
                  <motion.div key={item.title} whileHover={{ x: 6 }} className="benefit">
                    <div className="benefit-icon">{item.icon}</div>
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={.1}>
            <div className="form-box">
              {!formSent ? (
                <>
                  <h3 className="serif">Demander une démo</h3>
                  <p className="sub">Sans engagement · Réponse sous 24h</p>
                  <form onSubmit={handleForm}>
                    <div className="form-row">
                      <div className="field">
                        <label>Prénom</label>
                        <input value={formData.prenom} onChange={e => setFormData({...formData, prenom: e.target.value})} placeholder="Kofi" required />
                      </div>
                      <div className="field">
                        <label>Nom</label>
                        <input value={formData.nom} onChange={e => setFormData({...formData, nom: e.target.value})} placeholder="Mensah" required />
                      </div>
                      <div className="field full">
                        <label>Email</label>
                        <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="vous@email.com" required />
                      </div>
                      <div className="field">
                        <label>Téléphone</label>
                        <input value={formData.telephone} onChange={e => setFormData({...formData, telephone: e.target.value})} placeholder="+33 6 00 00 00 00" />
                      </div>
                      <div className="field">
                        <label>Pays du terrain</label>
                        <select value={formData.pays} onChange={e => setFormData({...formData, pays: e.target.value})}>
                          <option value="">Sélectionner</option>
                          {['Togo','Bénin',"Côte d'Ivoire",'Sénégal','Ghana','Cameroun','Mali','Burkina Faso','Guinée','RDC','Gabon'].map(p => <option key={p}>{p}</option>)}
                        </select>
                      </div>
                      <div className="field full">
                        <label>Votre projet</label>
                        <textarea value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} placeholder="Décrivez brièvement votre achat, vos doutes ou le terrain visé..." />
                      </div>
                    </div>
                    <button type="submit" className="btn-main" style={{ width:"100%", justifyContent:"center", marginTop:4 }} disabled={formLoading}>
                      {formLoading ? (
                        <motion.span animate={{ rotate:360 }} transition={{ repeat:Infinity, duration:.8, ease:"linear" }}
                          style={{ display:"inline-block", width:16, height:16, border:"2px solid rgba(255,255,255,.3)", borderTopColor:"white", borderRadius:"50%" }} />
                      ) : <>Obtenir ma démo gratuite <ArrowRight size={16} /></>}
                    </button>
                  </form>
                </>
              ) : (
                <motion.div className="success-box" initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}>
                  <div className="success-icon"><BadgeCheck size={34} /></div>
                  <h3 className="serif">Demande envoyée !</h3>
                  <p>Merci. Notre équipe vous recontacte rapidement pour organiser une démonstration personnalisée.</p>
                  <button className="btn-main" style={{ margin:"24px auto 0", display:"inline-flex" }} onClick={() => setFormSent(false)}>
                    Nouvelle demande
                  </button>
                </motion.div>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <a href="/#" className="logo">
                <div className="logo-mark"><MapPinned size={20} color={C.sand} /></div>
                <div className="logo-text serif">Terra<em>Track</em></div>
              </a>
              <p>Sécuriser l'investissement foncier de la diaspora, un dossier à la fois, avec davantage de preuve, de clarté et d'élégance dans l'expérience.</p>
            </div>
            <div className="footer-col">
              <strong>Produit</strong>
              <ul>
                <li><a href="#comment">Comment ça marche</a></li>
                <li><a href="#tarifs">Tarifs</a></li>
                <li><a href="#faq">FAQ</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <strong>Pays couverts</strong>
              <ul>
                {['Togo','Bénin',"Côte d'Ivoire",'Sénégal','Ghana'].map(p => <li key={p}><a href="#">{p}</a></li>)}
              </ul>
            </div>
            <div className="footer-col">
              <strong>Contact</strong>
              <ul>
                <li><a href="#demo" onClick={(e) => { e.preventDefault(); scrollToDemo(); }}>Demander une démo</a></li>
                <li><a href="/#">Se connecter</a></li>
                <li><a href="/#">Mentions légales</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <span>© 2025 TerraTrack. Tous droits réservés.</span>
            <span style={{ display:"inline-flex", gap:16 }}>
              <a href="/#">LinkedIn</a>
              <a href="/#">WhatsApp</a>
              <a href="/#">Twitter</a>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}